import { html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'

import { NordEvent } from '../common/events.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import componentStyle from '../common/styles/Component.css'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './TimePicker.css'
import '../input/Input.js'
import '../dropdown/Dropdown.js'
import '../dropdown-item/DropdownItem.js'
import '../button/Button.js'
import '../icon/Icon.js'

interface TimeOption {
  value: string
  label: string
}

/** Minutes since midnight from an ISO `HH:mm` value, or null. */
function isoToMinutes(value: string): number | null {
  const match = value.match(/^(\d{2}):(\d{2})$/)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

/** ISO `HH:mm` from minutes since midnight. */
function minutesToIso(total: number): string {
  const hours = Math.floor(total / 60) % 24
  return `${String(hours).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/**
 * Time Picker pairs the browser's native time field with a dropdown of quick
 * options, generated at a configurable `interval` between `start` and `end`.
 * The native field handles typing, locale-aware display and validation; the
 * dropdown is a shortcut for common times.
 *
 * The value is held in ISO `HH:mm` format.
 *
 * Like a native input, Time Picker is a control only — it has no built-in
 * label, hint or error. Compose it inside a [Field](/components/field/) (with a
 * [Field Label](/components/field-label/), [Field Description](/components/field-description/)
 * and [Field Error](/components/field-error/)) to label it and add help or error text.
 *
 * @status draft
 * @category form
 *
 * @fires change - Fired when the selected time changes.
 *
 * @localization chooseTime - Accessible label for the dropdown toggle button.
 */
@customElement('nord-time-picker')
export default class TimePicker extends DraftComponentMixin(LitElement) {
  static styles = [componentStyle, style]

  private localize = new LocalizeController<'nord-time-picker'>(this)

  private ariaObserver?: MutationObserver

  /** The selected time, in ISO `HH:mm` format. */
  @property() value?: string

  /** Name submitted with the form. */
  @property() name?: string

  /** Earliest time offered in the dropdown, ISO `HH:mm`. @default "09:00" */
  @property() start = '09:00'

  /** Latest time offered in the dropdown, ISO `HH:mm`. @default "17:00" */
  @property() end = '17:00'

  /** Minutes between each option in the dropdown. @default 30 */
  @property({ type: Number }) interval = 30

  /** When set, a typed time is rounded to the nearest interval option; otherwise any time is accepted. */
  @property({ type: Boolean, reflect: true, attribute: 'round-to-interval' }) roundToInterval = false

  /** Display dropdown times in 12-hour (AM/PM) or 24-hour format; omit to use the locale default. */
  @property({ reflect: true, attribute: 'hour-format' }) hourFormat?: '12h' | '24h'

  /** Earliest time the field will accept, ISO `HH:mm`. */
  @property() min?: string

  /** Latest time the field will accept, ISO `HH:mm`. */
  @property() max?: string

  /** Size of the field. @default "m" */
  @property({ reflect: true }) size: 's' | 'm' | 'l' = 'm'

  /** Whether the field expands to fill its container. */
  @property({ type: Boolean }) expand = false

  /** Whether the field is disabled. */
  @property({ type: Boolean }) disabled = false

  /** Whether the field is read-only. */
  @property({ type: Boolean }) readonly = false

  /** Whether a value is required when submitting a form. */
  @property({ type: Boolean }) required = false

  /**
   * Label for the control. Rendered by the inner input when used standalone;
   * ignored when wrapped in a `<nord-field>` (the Field provides the label).
   */
  @property() label?: string

  /** Optional hint text. Ignored inside a `<nord-field>`. */
  @property() hint?: string

  /** Optional error text. Ignored inside a `<nord-field>`. */
  @property() error?: string

  /** Visually hide the label, but keep it for assistive technology. */
  @property({ type: Boolean, attribute: 'hide-label' }) hideLabel = false

  /** Render the hint below the control instead of below the label. */
  @property({ type: Boolean, attribute: 'hint-below' }) hintBelow = false

  /** Visually hide the required indicator, but keep the required state for AT. */
  @property({ type: Boolean, attribute: 'hide-required' }) hideRequired = false

  @query('nord-input') private input?: HTMLElement & { focus: () => void, value: string }

  @query('nord-dropdown') private dropdown?: HTMLElement & { hide: () => void }

  connectedCallback() {
    super.connectedCallback()

    // Inside a <nord-field> the inner nord-input consumes the field context
    // directly (the request event crosses this shadow boundary), so it names and
    // describes itself. Standalone, it renders the forwarded label/hint/error.
    // We still re-render when a consumer sets aria-label on the host so it forwards.
    this.ariaObserver ??= new MutationObserver(() => this.requestUpdate())
    this.ariaObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-label', 'aria-labelledby'],
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.ariaObserver?.disconnect()
    this.ariaObserver = undefined
  }

  /** Move focus to the field, so a Field Label's click-to-focus works. */
  override focus(): void {
    this.input?.focus()
  }

  private get clampedInterval(): number {
    return this.interval > 1 && this.interval < 360 ? this.interval : 30
  }

  /**
   * Accessible name from a consumer-set host `aria-label` / `aria-labelledby`,
   * forwarded to the inner input as `aria-label` (an external `aria-labelledby`
   * can't cross the shadow boundary). Inside a `<nord-field>` the inner input
   * takes its name from the field context instead.
   */
  private get hostAccessibleName(): string {
    const ariaLabel = this.getAttribute('aria-label')
    if (ariaLabel) {
      return ariaLabel
    }
    const labelledby = this.getAttribute('aria-labelledby')
    if (!labelledby) {
      return ''
    }
    const root = this.getRootNode() as Document | ShadowRoot
    return labelledby
      .split(/\s+/)
      .map(id => root.getElementById?.(id)?.textContent?.trim() ?? '')
      .filter(Boolean)
      .join(' ')
  }

  /** Locale-aware label for a `HH:mm` value (matches how the native field reads). */
  private formatLabel(iso: string): string {
    const minutes = isoToMinutes(iso)
    if (minutes === null) {
      return iso
    }
    const date = new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)
    try {
      return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        ...(this.hourFormat ? { hour12: this.hourFormat === '12h' } : {}),
      }).format(date)
    }
    catch {
      return iso
    }
  }

  /** Generated dropdown options between `start` and `end`. */
  private get options(): TimeOption[] {
    const startMin = isoToMinutes(this.start) ?? 540
    const endMin = isoToMinutes(this.end) ?? 1020
    if (endMin < startMin) {
      return []
    }
    const out: TimeOption[] = []
    for (let m = startMin; m <= endMin; m += this.clampedInterval) {
      const iso = minutesToIso(m)
      out.push({ value: iso, label: this.formatLabel(iso) })
    }
    return out
  }

  /** Round a `HH:mm` value to the nearest interval option, clamped within `start`/`end`. */
  private roundToNearestInterval(iso: string): string {
    const minutes = isoToMinutes(iso)
    const startMin = isoToMinutes(this.start) ?? 540
    const endMin = isoToMinutes(this.end) ?? 1020
    if (minutes === null || endMin < startMin) {
      return iso
    }
    const step = this.clampedInterval
    const rounded = startMin + Math.round((minutes - startMin) / step) * step
    const clamped = Math.min(Math.max(rounded, startMin), endMin)
    return minutesToIso(clamped)
  }

  private setValue(iso: string): void {
    if (this.value === iso) {
      return
    }
    this.value = iso
    this.dispatchEvent(new NordEvent('change'))
  }

  private handleFieldChange = (event: Event): void => {
    const value = (event.target as HTMLInputElement).value
    this.setValue(this.roundToInterval ? this.roundToNearestInterval(value) : value)
  }

  private handleSelect(iso: string): void {
    this.setValue(iso)
    this.dropdown?.hide()
  }

  render() {
    return html`
      <nord-input
        type="time"
        label=${this.label ?? nothing}
        hint=${this.hint ?? nothing}
        error=${this.error ?? nothing}
        ?hide-label=${this.hideLabel}
        ?hint-below=${this.hintBelow}
        ?hide-required=${this.hideRequired}
        aria-label=${this.hostAccessibleName || nothing}
        name=${this.name ?? nothing}
        size=${this.size}
        min=${this.min ?? nothing}
        max=${this.max ?? nothing}
        ?expand=${this.expand}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?required=${this.required}
        .value=${this.value ?? ''}
        @change=${this.handleFieldChange}
      >
        <nord-dropdown slot="end" align="end" size="s">
          <nord-button slot="toggle" hide-dropdown-icon size=${this.size} square ?disabled=${this.disabled || this.readonly}>
            <nord-icon name="interface-time" label=${this.localize.term('chooseTime')}></nord-icon>
          </nord-button>
          ${repeat(
            this.options,
            option => option.value,
            option => html`
              <nord-dropdown-item
                ?active=${option.value === this.value}
                @click=${() => this.handleSelect(option.value)}
              >
                ${option.label}
              </nord-dropdown-item>
            `,
          )}
        </nord-dropdown>
      </nord-input>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-time-picker': TimePicker
  }
}
